import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { History as HistoryIcon, Trophy, ChefHat, X, Users } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { prettyDate } from "@/lib/dates";

export default function History() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [entries, setEntries] = useState([]);

  const load = () => {
    api.get("/history/favorites").then((r) => setFavorites(r.data));
    api.get("/history").then((r) => setEntries(r.data));
  };
  useEffect(() => { load(); }, []);

  const removeEntry = async (id) => { await api.delete(`/history/${id}`); toast.success("Retiré de l'historique"); load(); };

  const img = (path) => (path ? fileUrl(path) : null);

  return (
    <div className="px-5 pt-8" data-testid="history-page">
      <header className="at-fade-up">
        <div className="flex items-center gap-2">
          <HistoryIcon size={26} style={{ color: "var(--at-brand)" }} />
          <h1 className="font-display text-3xl font-bold">Historique</h1>
        </div>
        <p className="mt-1 text-sm" style={{ color: "var(--at-text-2)" }}>Les plats que vous avez cuisinés et les favoris de la famille.</p>
      </header>

      {/* Favoris de la famille */}
      <section className="mt-6 at-fade-up" style={{ animationDelay: "50ms" }} data-testid="family-favorites">
        <div className="mb-3 flex items-center gap-2">
          <Trophy size={20} style={{ color: "var(--at-honey)" }} />
          <h2 className="font-display text-xl font-semibold">Les favoris de la famille</h2>
        </div>
        <p className="mb-3 -mt-2 text-xs" style={{ color: "var(--at-muted)" }}>Vos plats les plus cuisinés — on écarte ceux déjà prévus récemment pour ne pas les reproposer d'une semaine sur l'autre.</p>
        {favorites.length === 0 ? (
          <div className="rounded-2xl border border-dashed p-6 text-center text-sm" style={{ borderColor: "var(--at-border)", color: "var(--at-muted)" }}>
            Cuisinez un plat et marquez-le comme préparé pour qu'il apparaisse ici.
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
            {favorites.map((r, i) => (
              <button key={r.id} data-testid={`fav-recipe-${r.id}`} onClick={() => navigate(`/recipes/${r.id}`)}
                className="at-press w-40 shrink-0 overflow-hidden rounded-2xl border border-black/5 bg-white text-left at-shadow at-fade-up" style={{ animationDelay: `${i * 45}ms` }}>
                <div className="relative aspect-video w-full overflow-hidden" style={{ background: "var(--at-elevated)" }}>
                  {img(r.images?.[0]) ? <img src={img(r.images[0])} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><ChefHat size={22} style={{ color: "var(--at-muted)" }} /></div>}
                  <span className="absolute left-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: "var(--at-honey)" }}>
                    {r.times_cooked}× cuisiné
                  </span>
                </div>
                <div className="p-2.5">
                  <p className="text-sm font-semibold leading-tight line-clamp-2">{r.title}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Historique récent */}
      <section className="mt-7 at-fade-up" style={{ animationDelay: "110ms" }}>
        <h2 className="mb-3 font-display text-xl font-semibold">Récemment préparés</h2>
        {entries.length === 0 ? (
          <div className="rounded-2xl border border-dashed p-6 text-center text-sm" style={{ borderColor: "var(--at-border)", color: "var(--at-muted)" }}>
            Aucun plat préparé pour l'instant.
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((e) => (
              <div key={e.id} data-testid={`history-entry-${e.id}`} className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white p-2.5 at-shadow">
                <button onClick={() => navigate(`/recipes/${e.recipe_id}`)} className="flex flex-1 items-center gap-3 text-left at-press">
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl" style={{ background: "var(--at-elevated)" }}>
                    {img(e.recipe_image) ? <img src={img(e.recipe_image)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><ChefHat size={18} style={{ color: "var(--at-muted)" }} /></div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium leading-tight">{e.recipe_title}</p>
                    <p className="flex items-center gap-2 text-xs" style={{ color: "var(--at-muted)" }}>
                      <span>{prettyDate(e.date)}</span>
                      {e.servings ? <span className="flex items-center gap-0.5"><Users size={11} /> {e.servings}</span> : null}
                    </p>
                  </div>
                </button>
                <button onClick={() => removeEntry(e.id)} data-testid={`remove-history-${e.id}`} style={{ color: "var(--at-muted)" }}><X size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
